import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import ApiClient from "../client";
import { ENDPOINTS } from "../endpoints";
import {
    UpdateFieldMappingsDto,
    UpdateCarrierMappingsDto,
    ProcessFileDto,
} from "../types";
import { queryKeys } from "../keys";


const client = new ApiClient(process.env.API_URL!, "app");

export const useProjectFileStats = (projectId: string) =>
    useQuery({
        queryKey: queryKeys.fileStats(projectId),
        queryFn: () => client.get(ENDPOINTS.PROJECT_FILE_STATS(projectId)),
        enabled: !!projectId,
    });

// Mutations
export const useUploadFile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ projectId, file }: { projectId: string; file: File }) => {
            const uploadUrlData = await client.post<{
                uploadUrl: string;
                s3Key: string;
                s3Bucket: string;
            }>(ENDPOINTS.FILE_UPLOAD_URL, {
                projectId,
                filename: file.name,
                contentType: file.type,
                fileSizeBytes: file.size,
            });

            await axios.put(uploadUrlData.uploadUrl, file, {
                headers: { "Content-Type": file.type },
            });

            const fileRecord = await client.post(ENDPOINTS.FILES, {
                projectId,
                filename: file.name,
                originalName: file.name,
                s3Key: uploadUrlData.s3Key,
                s3Bucket: uploadUrlData.s3Bucket,
                mimeType: file.type,
                fileSize: file.size,
            });

            return { fileRecord, projectId };
        },
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.files(projectId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.fileStats(projectId) });
            queryClient.invalidateQueries({ queryKey: ["companyProjects"], refetchType: "all" });
        },
    });
};


export const useProcessFile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ fileId, data }: { fileId: string; data: ProcessFileDto }) =>
            client.post(ENDPOINTS.FILE_PROCESS(fileId), data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["shipments"] });
            queryClient.invalidateQueries({ queryKey: ["shipments", "project"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            queryClient.invalidateQueries({ queryKey: ["companyProjects"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
            queryClient.invalidateQueries({ queryKey: ["shipmentEvents"] });
        },
    });
};
